import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiSearch, FiX } from 'react-icons/fi';

const SBFMemberPage = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalMembers, setTotalMembers] = useState(0);
    const [selectedMember, setSelectedMember] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [memberPayments, setMemberPayments] = useState([]);
    const [formData, setFormData] = useState({
        sbfAdsID: '',
        sbfAdsName: '',
        sbfQuote: '',
        adsCategory: 'ML',
        remarks: ''
    });

    const categories = ['ML', 'FL', 'Game', 'Vape'];
    const statuses = ['active', 'expiring', 'expired'];

    const fetchMembers = async (page, search = '', category = 'all', status = 'all') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/sbfmembers?page=${page}&limit=20`;
            
            if (search) apiUrl += `&search=${search}`;
            if (category !== 'all') apiUrl += `&category=${category}`;
            if (status !== 'all') apiUrl += `&status=${status}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setMembers(response.data.data);
                setTotalPages(response.data.pagination.pages);
                setTotalMembers(response.data.pagination.total);
            }
        } catch (err) {
            setError(`Failed to fetch SBF members: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers(currentPage, searchTerm, categoryFilter, statusFilter);
    }, [currentPage, searchTerm, categoryFilter, statusFilter]);

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/sbfmembers`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            setShowCreateForm(false);
            setFormData({ sbfAdsID: '', sbfAdsName: '', sbfQuote: '', adsCategory: 'ML', remarks: '' });
            fetchMembers(currentPage, searchTerm, categoryFilter, statusFilter);
        } catch (err) {
            setError(`Failed to create SBF member: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/sbfmembers/${selectedMember._id}`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            setShowEditModal(false);
            setSelectedMember(null);
            setFormData({ sbfAdsID: '', sbfAdsName: '', sbfQuote: '', adsCategory: 'ML', remarks: '' });
            fetchMembers(currentPage, searchTerm, categoryFilter, statusFilter);
        } catch (err) {
            setError(`Failed to update SBF member: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleDelete = (member) => {
        setSelectedMember(member);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/sbfmembers/${selectedMember._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowDeleteConfirm(false);
            setSelectedMember(null);
            fetchMembers(currentPage, searchTerm, categoryFilter, statusFilter);
        } catch (err) {
            setError(`Failed to delete SBF member: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleView = async (member) => {
        setSelectedMember(member);
        setShowViewModal(true);
        
        // Fetch payments for this member
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/sbfpayments/member/${member._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setMemberPayments(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch member payments:', err);
            setMemberPayments([]);
        }
    };

    const handleEdit = (member) => {
        setSelectedMember(member);
        setFormData({
            sbfAdsID: member.sbfAdsID,
            sbfAdsName: member.sbfAdsName,
            sbfQuote: member.sbfQuote,
            adsCategory: member.adsCategory,
            remarks: member.remarks || ''
        });
        setShowEditModal(true);
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setCategoryFilter('all');
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-SG');
    };

    const formatAmount = (amount, decimals = 2) => {
        if (!amount && amount !== 0) return 'N/A';
        return `$${Number(amount).toFixed(decimals)}`;
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">SBF Members</h1>
                        <p className="text-gray-600">Total: {totalMembers} members</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create New Member
                    </button>
                </div>

                {/* Search and Filter Bar */}
                <div className="mb-6">
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                        placeholder="Search by Ads ID, Ads Name, or Quote..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <FiSearch className="h-5 w-5 text-gray-400" />
                                    </div>
                                </div>
                                <button
                                    onClick={handleSearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                                >
                                    <FiSearch className="mr-2" />
                                    Search
                                </button>
                                <button
                                    onClick={handleClear}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <select
                                value={categoryFilter}
                                onChange={(e) => {
                                    setCategoryFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Status</option>
                                {statuses.map(status => (
                                    <option key={status} value={status}>
                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {showCreateForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                            <h2 className="text-xl font-bold mb-4">Create New SBF Member</h2>
                            <form onSubmit={handleCreateSubmit}>
                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            SBF Ads ID *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.sbfAdsID}
                                            onChange={(e) => setFormData({ ...formData, sbfAdsID: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            SBF Ads Name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.sbfAdsName}
                                            onChange={(e) => setFormData({ ...formData, sbfAdsName: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category *
                                        </label>
                                        <select
                                            required
                                            value={formData.adsCategory}
                                            onChange={(e) => setFormData({ ...formData, adsCategory: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Quote
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.sbfQuote}
                                            onChange={(e) => setFormData({ ...formData, sbfQuote: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Remarks
                                        </label>
                                        <textarea
                                            rows="2"
                                            value={formData.remarks}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="Optional remarks..."
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setFormData({ sbfAdsID: '', sbfAdsName: '', sbfQuote: '', adsCategory: 'ML', remarks: '' });
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Create
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showEditModal && selectedMember && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                            <h2 className="text-xl font-bold mb-4">Edit SBF Member</h2>
                            <form onSubmit={handleEditSubmit}>
                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            SBF Ads ID *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.sbfAdsID}
                                            onChange={(e) => setFormData({ ...formData, sbfAdsID: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            SBF Ads Name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.sbfAdsName}
                                            onChange={(e) => setFormData({ ...formData, sbfAdsName: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category *
                                        </label>
                                        <select
                                            required
                                            value={formData.adsCategory}
                                            onChange={(e) => setFormData({ ...formData, adsCategory: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Quote
                                        </label>
                                        <textarea
                                            rows="3"
                                            value={formData.sbfQuote}
                                            onChange={(e) => setFormData({ ...formData, sbfQuote: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Remarks
                                        </label>
                                        <textarea
                                            rows="2"
                                            value={formData.remarks}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="Optional remarks..."
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditModal(false);
                                            setSelectedMember(null);
                                            setFormData({ sbfAdsID: '', sbfAdsName: '', sbfQuote: '', adsCategory: 'ML', remarks: '' });
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Update
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showViewModal && selectedMember && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border max-w-3xl w-full shadow-lg rounded-md bg-white">
                            <div className="flex justify-between items-center border-b pb-4 mb-4">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-xl font-medium">
                                        SBF Member Details - <span className="ml-2 text-blue-700">{selectedMember.sbfAdsName || selectedMember.sbfAdsID}</span>
                                    </h3>
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full 
                                        ${selectedMember.status === 'active' ? 'bg-green-100 text-green-800' : 
                                          selectedMember.status === 'expired' ? 'bg-red-100 text-red-800' :
                                          selectedMember.status === 'expiring' ? 'bg-orange-100 text-orange-800' :
                                          'bg-gray-100 text-gray-800'}`}>
                                        {selectedMember.status || 'expired'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-4">
                                    {selectedMember.renewalDate && (
                                        <span className={`text-sm ${(() => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const renewalDateTime = new Date(selectedMember.renewalDate);
                                            renewalDateTime.setHours(0, 0, 0, 0);
                                            return renewalDateTime >= today ? 'text-green-600 font-bold' : 'text-red-600 font-bold';
                                        })()}`}>
                                            Renewal: {formatDate(selectedMember.renewalDate)}
                                        </span>
                                    )}
                                    <button 
                                        onClick={() => { 
                                            setShowViewModal(false); 
                                            setSelectedMember(null);
                                            setMemberPayments([]);
                                        }} 
                                        className="text-gray-500 hover:text-gray-700"
                                        aria-label="Close modal"
                                    >
                                        <FiX className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* Basic Information */}
                                <section>
                                    <h4 className="text-lg font-medium mb-3">Basic Information</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-gray-600 text-sm">SBF Ads ID:</span>
                                                <span className="ml-2 text-blue-700">{selectedMember.sbfAdsID}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 text-sm">SBF Ads Name:</span>
                                                <span className="ml-2 text-blue-700">{selectedMember.sbfAdsName || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-gray-600 text-sm">Category:</span>
                                                <span className="ml-2 text-blue-700">{selectedMember.adsCategory}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 text-sm">Quote:</span>
                                                <span className="ml-2 text-blue-700">{selectedMember.sbfQuote || 'N/A'}</span>
                                            </div>
                                        </div>
                                        {selectedMember.remarks && (
                                            <div>
                                                <span className="text-gray-600 text-sm">Remarks:</span>
                                                <span className="ml-2 text-blue-700">{selectedMember.remarks}</span>
                                            </div>
                                        )}
                                    </div>
                                </section>

                                {/* Payment Information */}
                                <section>
                                    <h4 className="text-lg font-medium mb-3">Payment Information</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                                        {/* Total Payment */}
                                        <div className="bg-white p-3 rounded">
                                            <h5 className="font-medium mb-2">Total Payment</h5>
                                            <div className="flex items-center gap-4">
                                                <span className="text-lg font-semibold text-green-700">
                                                    Total: {formatAmount(memberPayments.reduce((sum, payment) => sum + (payment.amountSGD || 0), 0), 2)}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* Payment History */}
                                        <div>
                                            <h5 className="font-medium mb-2">Payment History</h5>
                                            <div className="space-y-2">
                                                {memberPayments && memberPayments.length > 0 ? (
                                                    memberPayments.map((payment, index) => (
                                                        <div key={index} className="flex flex-wrap items-center gap-4 bg-white p-2 rounded">
                                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                                {payment.currency}
                                                            </span>
                                                            <span className="text-blue-700">Amount: {formatAmount(payment.amount, 3)} {payment.currency}</span>
                                                            <span className="text-blue-700">Rate: {payment.rate}</span>
                                                            <span className="text-green-600">Amount (SGD): {formatAmount(payment.amountSGD, 2)}</span>
                                                            <span className="text-blue-700">Payment Date: {formatDate(payment.paymentDate)}</span>
                                                            <span className="text-blue-700">Renewal Date: {formatDate(payment.renewalDate)}</span>
                                                            {payment.remarks && (
                                                                <span className="text-gray-600 w-full">Remarks: {payment.remarks}</span>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="text-gray-500">No payment history available</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                )}

                {showDeleteConfirm && selectedMember && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                            <p className="mb-6">Are you sure you want to delete "{selectedMember.sbfAdsName}"?</p>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => { setShowDeleteConfirm(false); setSelectedMember(null); }}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    SBF Ads ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    SBF Ads Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Category
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Quote
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total Amount
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : members.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-4 text-center">
                                        No SBF members found
                                    </td>
                                </tr>
                            ) : (
                                members.map((member) => (
                                    <tr key={member._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {member.sbfAdsID}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {member.sbfAdsName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                                ${member.adsCategory === 'ML' ? 'bg-blue-100 text-blue-800' : 
                                                  member.adsCategory === 'FL' ? 'bg-pink-100 text-pink-800' :
                                                  member.adsCategory === 'Game' ? 'bg-purple-100 text-purple-800' :
                                                  'bg-green-100 text-green-800'}`}>
                                                {member.adsCategory}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <div className="max-w-xs truncate">
                                                {member.sbfQuote}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                                ${member.status === 'active' ? 'bg-green-100 text-green-800' : 
                                                  member.status === 'expired' ? 'bg-red-100 text-red-800' :
                                                  member.status === 'expiring' ? 'bg-orange-100 text-orange-800' :
                                                  'bg-gray-100 text-gray-800'}`}>
                                                {member.status || 'expired'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-700">
                                            {formatAmount(member.totalAmount || 0, 2)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button
                                                onClick={() => handleView(member)}
                                                className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                title="View Details"
                                            >
                                                <FiEye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(member)}
                                                className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                title="Edit Member"
                                            >
                                                <FiEdit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(member)}
                                                className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                title="Delete Member"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        </Layout>
    );
};

export default SBFMemberPage;

